import React, { useContext } from 'react';
import PropTypes from 'prop-types';

import { getAvailableReportText, getUnavailableReportText } from '../../../../../../utils/applicantDetailsUtil';
import { backgroundStatusTypes, creditStatusTypes, idStatusTypes } from '../../../../../../constants/statusConstants';

import UserNotFoundSection from './UserNotFoundSection';
import Description from './Description';
import SectionComment from './SectionComment';
import SummaryNavLink from '../SummaryNavLink';
import SummaryNavLinkButton from '../SummaryNavLinkButton';

import { and } from 'airbnb-prop-types';
import { passthroughProp } from '../../../../../../utils/propTypes';
import { IcraaComplianceContext } from '../../../../../../contexts/IcraaComplianceContext';
import { useApplicationSummaryContext } from '../../../../../../contexts/ApplicationSummaryContext';
import { Alert } from '@zillow/constellation';

export default function ScreeningReportNavLink({
  sectionId,
  applicant,
  sectionComment,
  reportStatus,
  title,
  trackNavLinkClick,
  isCustomLoading,
  setCustomNavigationCallback,
  alert,
}) {
  const { isLoading, hasComplianceError, onNavLinkClick } = useContext(IcraaComplianceContext);
  const { isApplicationDeclined } = useApplicationSummaryContext();

  const { idVerificationInfo = {}, screeningReportShareInfo = {}, isComplete } = applicant;

  if (idVerificationInfo.status === idStatusTypes.USER_NOT_FOUND) {
    return <UserNotFoundSection applicant={applicant} sectionComment={sectionComment} title={title} />;
  }

  const unavailableReportText = getUnavailableReportText({
    hasComplianceError,
    screeningReportShareInfo,
    reportStatus,
    isComplete,
    isDeclined: isApplicationDeclined,
  });

  // Get available report text if there is no unavailable report text.
  const reportText = unavailableReportText ? (
    <Alert appearance="warning">{unavailableReportText}</Alert>
  ) : (
    getAvailableReportText(screeningReportShareInfo)
  );

  return (
    <SummaryNavLink
      {...{
        title,
        description: (
          <Description
            {...{
              applicant,
              isLoading,
              reportText,
              sectionComment,
            }}
          />
        ),
        // Don't show the view button if no report is available.
        ...(!unavailableReportText && {
          button: (
            <SummaryNavLinkButton
              {...{
                applicant,
                sectionId,
                title,
                isCustomLoading,
                customShowNavLinkAsButton: Boolean(setCustomNavigationCallback),
                onClick: () => {
                  onNavLinkClick({
                    sectionId,
                    onAllCompliantCallback: trackNavLinkClick,
                    setCustomNavigationCallback,
                  });
                },
              }}
            />
          ),
        }),
        alert,
      }}
    />
  );
}

ScreeningReportNavLink.propTypes = {
  title: PropTypes.string.isRequired,
  sectionId: PropTypes.string.isRequired,
  trackNavLinkClick: PropTypes.func.isRequired,
  isCustomLoading: PropTypes.bool,
  setCustomNavigationCallback: PropTypes.func,
  applicant: PropTypes.shape({
    idVerificationInfo: PropTypes.shape({
      status: PropTypes.string.isRequired,
    }),
    screeningReportShareInfo: PropTypes.shape({
      status: PropTypes.string.isRequired,
      expiration: PropTypes.number.isRequired,
    }),
    isComplete: PropTypes.bool.isRequired,
  }).isRequired,
  reportStatus: PropTypes.oneOf([...Object.values(backgroundStatusTypes), ...Object.values(creditStatusTypes)]),
  sectionComment: and([passthroughProp(UserNotFoundSection), passthroughProp(SectionComment)]),
  alert: PropTypes.node,
};

ScreeningReportNavLink.displayName = 'ScreeningReportNavLink';
